/* The receipt QR is generated at submission, not embedded in the UBL, so the test
   document shows the values it will carry, read back out of the rendered invoice.
   The seven mirror gateway/qr.py::QR_TAG_FIELDS tags 3-9; tags 1 and 2 are constants. */

export type QrValue = { label: string; value: string | null };

function childrenNamed(parent: Element, localName: string): Element[] {
  return Array.from(parent.children).filter((child) => child.localName === localName);
}

function textAt(root: Element, path: string[]): string | null {
  let node: Element | null = root;
  for (const step of path) {
    node = node ? (childrenNamed(node, step)[0] ?? null) : null;
  }
  const text = node?.textContent?.trim();
  return text ? text : null;
}

/** The document-currency TaxTotal; a non-OMR invoice carries a tax-currency sibling. */
function vatAmount(root: Element): string | null {
  const currency = textAt(root, ["DocumentCurrencyCode"]);
  const amounts = childrenNamed(root, "TaxTotal")
    .map((total) => childrenNamed(total, "TaxAmount")[0])
    .filter((node): node is Element => node !== undefined);
  const matched = currency
    ? amounts.find((node) => node.getAttribute("currencyID") === currency)
    : undefined;
  const text = (matched ?? amounts[0])?.textContent?.trim();
  return text ? text : null;
}

/** Paths by local name, taken from gateway/ubl.py's field map. */
const QR_READERS: [string, (root: Element) => string | null][] = [
  ["Seller name", (r) =>
    textAt(r, ["AccountingSupplierParty", "Party", "PartyLegalEntity", "RegistrationName"])],
  ["Seller VAT number", (r) =>
    textAt(r, ["AccountingSupplierParty", "Party", "PartyTaxScheme", "CompanyID"])],
  ["Issue date", (r) => textAt(r, ["IssueDate"])],
  ["Issue time", (r) => textAt(r, ["IssueTime"])],
  ["Total including VAT", (r) => textAt(r, ["LegalMonetaryTotal", "TaxInclusiveAmount"])],
  ["VAT amount", vatAmount],
  // BTOM-004, derived for B2C invoices only, so a B2B invoice legitimately has none.
  ["Seller UUID", (r) => textAt(r, ["AccountingSupplierParty", "AdditionalAccountID"])],
];

/** The seven values this invoice's receipt QR carries; a null is a value the invoice omits. */
export function qrInformation(xml: string): QrValue[] {
  if (!xml.trim()) return [];
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const root = doc.documentElement;
  if (!root || doc.getElementsByTagName("parsererror").length > 0) return [];
  return QR_READERS.map(([label, read]) => ({ label, value: read(root) }));
}
